import { apiUrl } from "../api/endpoints";
import React, { useContext, useState } from "react";
import axios from "axios";

import { getErrorMessage } from "../utils/errorUtils";
import { LanguageContext } from "../context/LanguageContext";
import permissionTranslations from "../languages/permission.json";
import "../Components/modal.css";
import "../Pages/Permissions.css";

const formatTranslation = (template, values = {}) =>
  Object.entries(values).reduce(
    (message, [key, value]) => message.replaceAll(`{${key}}`, value),
    template || ""
  );

const EditPermissionDataModal = ({ permission, onClose, onUpdate, showAlert }) => {
  const { lang } = useContext(LanguageContext);
  const translations = permissionTranslations[lang] || permissionTranslations.en;
  const t = translations.editDataModal;
  const isRtl = ["ar", "ur"].includes(lang);

  const [formData, setFormData] = useState({
    name: permission.name,
    category: permission.category,
    subcategory: permission.subcategory || "",
    ids: permission.ids
  });
  const [saving, setSaving] = useState(false);

  const isDirty =
    formData.name !== permission.name ||
    formData.category !== permission.category ||
    formData.subcategory !== (permission.subcategory || "") ||
    formData.ids !== permission.ids;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!isDirty) {
      showAlert("info", t.noChangesToSave);
      onClose();
      return;
    }

    if (!formData.name || !formData.category || !formData.ids) {
      showAlert("error", t.requiredFields);
      return;
    }

    setSaving(true);

    try {
      await axios.post(apiUrl(`permission/update/${permission.id}`), {
        name: formData.name,
        category: formData.category,
        subcategory: formData.subcategory,
        ids: formData.ids
      });

      showAlert("success", formatTranslation(t.updateSuccess, { name: formData.name }));
      onUpdate();
      onClose();
    } catch (err) {
      showAlert("error", formatTranslation(t.updateFailed, { error: getErrorMessage(err) }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div id="modal" onClick={onClose} dir={isRtl ? "rtl" : "ltr"}>
      <div
        id="popup"
        className="dashboard-modal permissions-dialog permissions-edit-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dashboard-header">
          <div className="dashboard-header-text">
            <h2>{t.title}</h2>
            <p>
              {t.permissionLabel}: <b>{permission.name}</b>
            </p>
          </div>
        </div>

        <div className="modal-divider" />

        <div className="modal-content dashboard-content">
          <div className="permissions-modal-note">{t.subtitle}</div>

          <div className="permissions-edit-form">
            <div className="form-item">
              <label className="form-lbl">{t.idsLabel}</label>
              <input
                type="text"
                className="add-column"
                name="ids"
                value={formData.ids}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-item">
              <label className="form-lbl">{t.nameLabel}</label>
              <input
                type="text"
                className="add-column"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-item">
              <label className="form-lbl">{t.categoryLabel}</label>
              <input
                type="text"
                className="add-column"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-item">
              <label className="form-lbl">{t.subcategoryLabel}</label>
              <input
                type="text"
                className="add-column"
                name="subcategory"
                value={formData.subcategory}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        <div className="modal-footer dashboard-footer permissions-modal-footer">
          <div className="modal-footer-right-buttons">
            <button className="btn btn-cancel" type="button" onClick={onClose} disabled={saving}>
              {t.buttonCancel}
            </button>
            <button
              className="btn btn-apply dashboard-primary-btn"
              type="button"
              onClick={handleSave}
              disabled={!isDirty || saving}
            >
              {saving ? t.buttonSaving : t.buttonSave}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPermissionDataModal;
